import { useEffect, useState } from 'react';

import { ClickAwayListener, useTheme } from '@mui/material';

import { useIsTouchDevice } from '@cast/utils';

import {
  HEADER_HEIGHT,
  SECONDARY_SIDEBAR_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
} from 'main-layout-op/_constants';
import { useMainLayout } from 'main-layout-op/_providers/MainLayoutProvider';

import { SecondarySidebar } from './SecondarySidebar';
import { SidebarCollapseButton } from './SecondarySidebar/_components/SidebarCollapseButton';
import { SidebarClusterProvider } from './SidebarClusterProvider';

import { Box, Stack, tokens } from '@cast/design-system';

export function Sidebar() {
  const isTouch = useIsTouchDevice();
  const theme = useTheme();
  const {
    sidebarRef,
    secondarySidebarState,
    collapseSecondarySidebar,
    handleSidebarEnter,
    handleSidebarLeave,
  } = useMainLayout();

  const isCollapsed = secondarySidebarState === 'collapsed';
  const [isSidebarFullyCollapsed, setIsSidebarFullyCollapsed] = useState(false);

  // Reset every time a new collapse begins so white bg never shows mid-transition
  useEffect(() => {
    if (isCollapsed) {
      setIsSidebarFullyCollapsed(false);
    }
  }, [isCollapsed]);

  const handleClickAway = () => {
    // On touch devices, collapse the sidebar when clicking outside (only if expanded, not pinned)
    if (isTouch && secondarySidebarState === 'expanded') {
      collapseSecondarySidebar();
    }
  };

  return (
    <SidebarClusterProvider>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Stack
          position="sticky"
          top="var(--escalation-header-height, 0px)"
          height="calc(100dvh - var(--escalation-header-height, 0px))"
          sx={{
            gridColumn: 'sidebar',
            gridRow: 'toolbar / -1',
            zIndex: 2,
            overflow: 'visible',
          }}
          onPointerEnter={() => {
            if (isTouch) {
              return;
            }
            handleSidebarEnter();
          }}
          onPointerLeave={() => {
            if (isTouch) {
              return;
            }
            handleSidebarLeave();
          }}
          data-testid="sidebar"
        >
          {/* Transparent spacer: fixed height matching the app header */}
          <Box sx={{ height: HEADER_HEIGHT, flexShrink: 0 }} />

          {/* Sidebar content: fills the remaining viewport height */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              width: isCollapsed
                ? SIDEBAR_COLLAPSED_WIDTH
                : SECONDARY_SIDEBAR_WIDTH,
              transition: `width ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`,
            }}
            onTransitionEnd={(e) => {
              if (e.propertyName === 'width') {
                setIsSidebarFullyCollapsed(isCollapsed);
              }
            }}
          >
            {isCollapsed ? (
              <Stack height="100%" bgcolor="grey.100">
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="center"
                  pt={8}
                  pr={8}
                  pb={8}
                  pl={12}
                  sx={{
                    mt: 16,
                    borderRadius: '0px 4px 4px 0px',
                    bgcolor: isSidebarFullyCollapsed
                      ? tokens.theme.bg.structural.page
                      : 'transparent',
                    boxShadow: isSidebarFullyCollapsed
                      ? 'hoverComponent'
                      : 'none',
                    transition: isSidebarFullyCollapsed
                      ? `background-color ${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeInOut}, box-shadow ${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeInOut}`
                      : 'none',
                  }}
                >
                  <SidebarCollapseButton />
                </Stack>
              </Stack>
            ) : (
              <SecondarySidebar ref={sidebarRef} />
            )}
          </Box>
        </Stack>
      </ClickAwayListener>
    </SidebarClusterProvider>
  );
}
